/* =============================================================================
 * Delta Clothing — payment method radios + 3.5% surcharge UI
 *
 * Drives the radio group dropped into the cart page / drawer footer. Updates
 *
 *   [data-cart-subtotal-value]   (data attr + label)
 *   [data-cart-fee-value]        (data attr + label, row hidden when zero)
 *   [data-cart-total-value]      (data attr + label)
 *
 * and posts to /payment so the Laravel DraftOrder gets the fee line added
 * before the customer clicks Checkout.
 *
 * Mirrors new-ella sections/main-cart.liquid (_calcFee, _refreshSummary,
 * applyPaymentMethod, _renderPaymentUIState, lines 398-760).
 *
 * sessionStorage contract preserved verbatim:
 *   __dlc_payment_method        ("cash_on_hand" | "credit_card" | "paypal")
 *   __dlc_payment_pending       ("1" while /payment is in-flight)
 *
 * Depends on window.B2B (b2b-config.js).
 * =============================================================================*/
(function () {
  if (!window.B2B || window.__dlc_payment_fee_installed) return;
  window.__dlc_payment_fee_installed = true;

  var B2B = window.B2B;
  var PAYMENT_FEE_RATE = B2B.PAYMENT_FEE_RATE || 0.035;

  function getMethod() {
    try { return (sessionStorage.getItem('__dlc_payment_method') || '').trim(); }
    catch (e) { return ''; }
  }
  function setMethod(v) {
    try {
      if (v) sessionStorage.setItem('__dlc_payment_method', String(v));
      else sessionStorage.removeItem('__dlc_payment_method');
    } catch (e) {}
  }
  function setPending(flag) {
    try { sessionStorage.setItem('__dlc_payment_pending', flag ? '1' : '0'); } catch (e) {}
  }
  function isPending() {
    try { return (sessionStorage.getItem('__dlc_payment_pending') || '') === '1'; }
    catch (e) { return false; }
  }
  function clearMethod() { setMethod(''); setPending(false); }
  function round2(n) { return Math.round((parseFloat(n) || 0) * 100) / 100; }
  function money(n) { return '$' + (parseFloat(n) || 0).toFixed(2); }

  function calcFee(subtotal) {
    var pm = getMethod();
    if (!pm || pm === 'cash_on_hand') return 0;
    return round2((parseFloat(subtotal) || 0) * PAYMENT_FEE_RATE);
  }

  function refreshSummary(subtotal) {
    subtotal = parseFloat(subtotal) || 0;
    var fee = calcFee(subtotal);
    var total = subtotal + fee;

    document.querySelectorAll('[data-cart-subtotal-value]').forEach(function (el) {
      el.setAttribute('data-cart-subtotal-value', subtotal.toFixed(2));
      var s = el.querySelector('[data-text]') || el.querySelector('span') || el;
      s.textContent = money(subtotal);
    });

    document.querySelectorAll('[data-cart-fee-row]').forEach(function (row) {
      row.style.display = fee > 0 ? '' : 'none';
    });
    document.querySelectorAll('[data-cart-fee-value]').forEach(function (el) {
      el.setAttribute('data-cart-fee-value', fee.toFixed(2));
      var s = el.querySelector('[data-text]') || el.querySelector('span') || el;
      s.textContent = money(fee);
    });

    document.querySelectorAll('[data-cart-total-value]').forEach(function (el) {
      el.setAttribute('data-cart-total-value', total.toFixed(2));
      var s = el.querySelector('[data-text]') || el.querySelector('span') || el;
      s.textContent = money(total);
    });
  }

  function recomputeFromRows() {
    var t = 0;
    document.querySelectorAll('[data-cart-content] [data-price]').forEach(function (cell) {
      var p = parseFloat(cell.getAttribute('data-price'));
      if (!isNaN(p)) t += p;
    });
    if (!t) {
      // Fall back to summing line cells outside [data-cart-content]
      document.querySelectorAll('.cart-item-total[data-price]').forEach(function (cell) {
        var p = parseFloat(cell.getAttribute('data-price'));
        if (!isNaN(p)) t += p;
      });
    }
    refreshSummary(t);
  }

  // Cached "is the customer over their credit limit?" flag. Fetched once
  // at boot via /api/customer-limits/{cid} and refreshed when the cart
  // changes. While true we lock the Cash on Hand radio (purchase limit
  // only restricts on-account / Net-30 payment) and surface a hint
  // pointing the customer at Credit Card / PayPal — those don't add to
  // the unpaid balance so they're allowed even when the limit is full.
  // See app/Http/Controllers/ThemeApiController::limit() (overLimit flag).
  var __limitFull = false;
  var __limitFetchPromise = null;

  function fetchOverLimit() {
    if (__limitFetchPromise) return __limitFetchPromise;
    var cust = (document.body && document.body.getAttribute('customer')) || '';
    if (!cust) {
      __limitFull = false;
      return Promise.resolve(false);
    }
    __limitFetchPromise = B2B.get(B2B.endpoints.customerLimits(cust))
      .then(function (r) {
        var d = (r && r.data) || {};
        // 404 / NO_POLICY means "no limit on file" — let everything through.
        if (r.status === 404 || d.error || /not found/i.test(String(d.message || ''))) {
          __limitFull = false;
          return false;
        }
        __limitFull = !!d.overLimit;
        return __limitFull;
      })
      .catch(function () {
        __limitFull = false;
        return false;
      })
      .finally(function () {
        // Allow the next refresh to re-fetch (called from dlc:cart-changed)
        setTimeout(function () { __limitFetchPromise = null; }, 0);
      });
    return __limitFetchPromise;
  }

  function renderUIState() {
    var pm = getMethod();
    var pending = isPending();
    document.querySelectorAll('input[name="dlc_payment_method"]').forEach(function (r) {
      var lockForLimit = __limitFull && r.value === 'cash_on_hand';
      r.disabled = pending || lockForLimit;
      r.checked = !!pm && (r.value === pm);
      // Visually mute the option label when locked
      var label = r.closest('label');
      if (label) label.classList.toggle('b2b-cart__payment-option--locked', lockForLimit);
    });
    document.querySelectorAll('[data-payment-status]').forEach(function (st) {
      if (pending) {
        st.style.display = 'block';
        st.textContent = 'Updating payment method...';
      } else {
        st.style.display = 'none';
        st.textContent = '';
      }
    });
    // Limit-full warning hint (shown above/below the radio group)
    document.querySelectorAll('[data-payment-limit-warning]').forEach(function (w) {
      if (__limitFull) {
        w.style.display = 'block';
        if (!w.textContent.trim()) {
          w.textContent = 'Your purchase limit is full. Cash on Hand is unavailable — please pay with Credit Card or PayPal.';
        }
      } else {
        w.style.display = 'none';
      }
    });
    // If the customer had Cash on Hand selected before going over limit,
    // clear the stored choice so the checkout guard can prompt them to
    // pick a different method (instead of silently bouncing them off
    // the limit modal at checkout time).
    if (__limitFull && pm === 'cash_on_hand') {
      clearMethod();
    }
  }

  function applyMethod(method) {
    if (!method) return;
    var cust = B2B.getCustomerGid();
    if (!cust) { alert('Please sign in to continue.'); return; }
    setMethod(method);
    setPending(true);
    renderUIState();

    // Optimistic UI: re-render with the new fee immediately
    var subEl = document.querySelector('[data-cart-subtotal-value]');
    var sub = subEl ? parseFloat(subEl.getAttribute('data-cart-subtotal-value')) : NaN;
    if (!isNaN(sub)) refreshSummary(sub);
    else recomputeFromRows();

    var draftId = (function () {
      try {
        var cache = window.__dlc_bag && window.__dlc_bag.getCache && window.__dlc_bag.getCache();
        return (cache && cache[0] && cache[0].draft_id) || null;
      } catch (e) { return null; }
    })();

    B2B.postJSON(B2B.endpoints.payment, {
      customer_id: cust,
      draft_id: draftId,
      payment_method: method
    }).then(function (resp) {
      setPending(false);
      if (!resp || resp.error || resp.ok === false) {
        clearMethod();
        renderUIState();
        if (!isNaN(sub)) refreshSummary(sub);
        alert('Payment method update failed. Please try again.');
        return;
      }
      renderUIState();
      if (resp && resp.invoiceUrl) {
        document.querySelectorAll('.invoiceUrl,[data-checkout-href]').forEach(function (a) {
          a.setAttribute('href', resp.invoiceUrl);
        });
      }
    }).catch(function (err) {
      setPending(false);
      clearMethod();
      renderUIState();
      if (!isNaN(sub)) refreshSummary(sub);
      console.error(err);
      alert('Payment method update failed. Please try again.');
    });
  }

  // Listen to payment-method radio changes
  document.addEventListener('change', function (e) {
    var r = e.target && e.target.closest && e.target.closest('input[name="dlc_payment_method"]');
    if (!r) return;
    // Defensive: if the radio is somehow toggled while disabled (some
    // Stockmart skins reset radios on focus), bail.
    if (r.disabled) return;
    applyMethod(r.value);
  }, true);

  // When the cart store paints fresh lines (cart page / drawer), recompute
  // both the totals and the limit status — paying down an invoice in the
  // background should re-enable Cash on Hand without a full page reload.
  window.addEventListener('dlc:cart-changed', function () {
    setTimeout(function () {
      fetchOverLimit().then(renderUIState);
      recomputeFromRows();
    }, 30);
  });

  // Recompute on bootstrap once rows exist
  function init() {
    fetchOverLimit().then(renderUIState);
    renderUIState();
    recomputeFromRows();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    setTimeout(init, 0);
  }

  // Expose for inline rendering callbacks
  window.__dlc_payment_fee = {
    refreshSummary: refreshSummary,
    recomputeFromRows: recomputeFromRows,
    renderUIState: renderUIState,
    applyMethod: applyMethod,
    getMethod: getMethod,
    clearMethod: clearMethod,
    isOverLimit: function () { return __limitFull; },
    refreshLimit: function () { return fetchOverLimit().then(renderUIState); }
  };
})();
